/**
 * Renders the Foxhole "subregion" control overlay: a Voronoi diagram of every
 * Town/Relic base in each region, coloured by the controlling faction. Mounted
 * inside the {@link FoxholeAtlas} overlay slot so it shares the SVG viewport.
 *
 * The layer is purely decorative — it never intercepts pointer events.
 */
import { useMemo } from 'react';
import { ATLAS, HEX_VERTICES, findNearestTile } from '../lib/foxhole-maps.js';
import {
  SUBREGION_BASE_ICON_TYPES,
  SUBREGION_OPACITY,
  SUBREGION_STROKE,
  SUBREGION_STROKE_OPACITY,
  computeSubregionCells,
  type SubregionBase,
  type SubregionCell,
} from '../lib/foxhole-subregions.js';

export interface SubregionsLayerIcon {
  iconType: number;
  teamId: string | null;
  /** World-space pixel coordinates (absolute). */
  wx: number;
  wy: number;
}

export interface SubregionsLayerProps {
  icons: readonly SubregionsLayerIcon[];
  disabledRegionKeys?: ReadonlySet<string> | undefined;
}

const HEX_CLIP_ID = 'subregions-hex-clip';
const HEX_POINTS = HEX_VERTICES.map((p) => `${p.x},${p.y}`).join(' ');

interface TileCells {
  tileKey: string;
  tileX: number;
  tileY: number;
  cells: SubregionCell[];
}

export function SubregionsLayer({
  icons,
  disabledRegionKeys,
}: SubregionsLayerProps): React.JSX.Element | null {
  const tileCells = useMemo<TileCells[]>(() => {
    // Group eligible bases per tile, in tile-local coordinates.
    const byTile = new Map<string, { tileX: number; tileY: number; bases: SubregionBase[] }>();
    for (const icon of icons) {
      if (!SUBREGION_BASE_ICON_TYPES.has(icon.iconType)) continue;
      const tile = findNearestTile(icon.wx, icon.wy);
      if (!tile) continue;
      if (disabledRegionKeys?.has(tile.key)) continue;
      let entry = byTile.get(tile.key);
      if (!entry) {
        entry = { tileX: tile.x, tileY: tile.y, bases: [] };
        byTile.set(tile.key, entry);
      }
      entry.bases.push({
        x: icon.wx - tile.x,
        y: icon.wy - tile.y,
        teamId: icon.teamId,
      });
    }
    // Iterate tiles in ATLAS order for a stable render order.
    const out: TileCells[] = [];
    for (const tile of ATLAS) {
      const entry = byTile.get(tile.key);
      if (!entry) continue;
      const cells = computeSubregionCells(entry.bases);
      if (cells.length === 0) continue;
      out.push({ tileKey: tile.key, tileX: entry.tileX, tileY: entry.tileY, cells });
    }
    return out;
  }, [icons, disabledRegionKeys]);

  if (tileCells.length === 0) return null;

  // All non-disabled tiles get an outer hex border so the boundary of each
  // region is clearly closed, even when a tile has no subregion data.
  const activeTileKeys = new Set(tileCells.map((tc) => tc.tileKey));
  const allActiveTiles = ATLAS.filter(
    (t) => !disabledRegionKeys?.has(t.key),
  );

  return (
    <g pointerEvents="none" data-layer="subregions">
      <defs>
        <clipPath id={HEX_CLIP_ID} clipPathUnits="userSpaceOnUse">
          <polygon points={HEX_POINTS} />
        </clipPath>
      </defs>

      {/* Coloured Voronoï cells, clipped to each hex */}
      {tileCells.map(({ tileKey, tileX, tileY, cells }) => (
        <g
          key={tileKey}
          transform={`translate(${tileX} ${tileY})`}
          clipPath={`url(#${HEX_CLIP_ID})`}
        >
          {cells.map((cell, idx) => (
            <path
              key={idx}
              d={cell.d}
              fill={cell.color}
              fillOpacity={SUBREGION_OPACITY}
              stroke={SUBREGION_STROKE}
              strokeOpacity={SUBREGION_STROKE_OPACITY}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
            />
          ))}
        </g>
      ))}

      {/* Outer hex border on every active tile — closes the region boundary */}
      {allActiveTiles.map((tile) => (
        <polygon
          key={`hex-border-${tile.key}`}
          transform={`translate(${tile.x} ${tile.y})`}
          points={HEX_POINTS}
          fill="none"
          stroke={SUBREGION_STROKE}
          strokeOpacity={activeTileKeys.has(tile.key) ? SUBREGION_STROKE_OPACITY : 0.3}
          strokeWidth={activeTileKeys.has(tile.key) ? 1.5 : 1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </g>
  );
}
